import type {
  CopilotProposalStatus,
  CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import type { FC } from "react";
import { useLocation, useNavigate, type To } from "react-router";
import { useAppSelector } from "../../hooks/hook.js";
import { readAutomationProposalCardContent } from "../automations/automationProposalCardContent.js";
import { readSignalProposalCardContent } from "../settingsPage/signalSettings/signalProposalCardContent.js";
import {
  ConfigurationProposalCard,
  ConfigurationProposalStatus,
} from "./configurationProposalCard.js";
import { selectEditorProposalStatus } from "./editorProposalsSlice.js";

/** What a resource type contributes to its proposal's card. */
export interface CopilotProposalCardContent {
  summary: string;
  highlights: readonly string[];
  /** The editor that puts the pending proposal into its draft. */
  editLocation: To;
  /** The resource the proposal is about, once it is resolved. */
  resourcePathname: string;
}

export interface CopilotProposalCardProps {
  proposal: CopilotProposalSummary<"signal" | "automation">;
}

/**
 * A copilot proposal in the chat. Review opens the editor with the change in
 * its draft, where Save applies it; Open shows the resource a resolved one is
 * about, unless it is already open. While the open editor holds the change,
 * the card shows it as Viewing, then Saving.
 */
export const CopilotProposalCard: FC<CopilotProposalCardProps> = (props) => {
  const { proposal } = props;
  const { summary, highlights, editLocation, resourcePathname } =
    readCardContent(proposal);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const editorStatus = useAppSelector((state) =>
    selectEditorProposalStatus(state, proposal.id),
  );
  const status =
    proposal.status === "pending" && editorStatus
      ? editorStatus
      : CARD_STATUSES[proposal.status];
  const destination = resolveDestination(status, {
    editLocation,
    resourcePathname,
    currentPathname: pathname,
  });

  return (
    <ConfigurationProposalCard
      resourceType={proposal.resource_type}
      operation={proposal.kind.startsWith("create_") ? "create" : "update"}
      resourceName={proposal.resource.name}
      summary={summary}
      highlights={highlights}
      status={status}
      onAction={
        destination
          ? () => {
              void navigate(destination);
            }
          : undefined
      }
    />
  );
};

function readCardContent(
  proposal: CopilotProposalSummary<"signal" | "automation">,
): CopilotProposalCardContent {
  switch (proposal.resource_type) {
    case "signal":
      return readSignalProposalCardContent(proposal);
    case "automation":
      return readAutomationProposalCardContent(proposal);
  }
}

const CARD_STATUSES: Record<
  CopilotProposalStatus,
  ConfigurationProposalStatus
> = {
  pending: ConfigurationProposalStatus.Pending,
  applied: ConfigurationProposalStatus.Applied,
  discarded: ConfigurationProposalStatus.Outdated,
  stale: ConfigurationProposalStatus.Outdated,
};

function resolveDestination(
  status: ConfigurationProposalStatus,
  locations: {
    editLocation: To;
    resourcePathname: string;
    currentPathname: string;
  },
): To | undefined {
  switch (status) {
    case ConfigurationProposalStatus.Pending:
      return locations.editLocation;
    case ConfigurationProposalStatus.Applied:
    case ConfigurationProposalStatus.Outdated:
      return locations.resourcePathname === locations.currentPathname
        ? undefined
        : { pathname: locations.resourcePathname };
    case ConfigurationProposalStatus.Viewing:
    case ConfigurationProposalStatus.Saving:
      return undefined;
  }
}
